"use client";

import {
  Fragment,
  useEffect,
  useLayoutEffect,
  useRef,
  type CSSProperties,
  type ElementType,
  type ReactNode,
} from "react";
import { gsap, PLAY_ONCE, useGSAP } from "@/lib/gsap";
import { blurReveal, clipReveal, maskReveal, reduceMotion } from "@/lib/motion";
import { cn } from "@/lib/utils";

const useIsoLayoutEffect = typeof window !== "undefined" ? useLayoutEffect : useEffect;

type Split = "words" | "chars" | "lines";
type Variant = "blur" | "mask" | "clip";

export type TextRevealProps = {
  /** Plain text. Use "\n" to force a line break. */
  text: string;
  as?: ElementType;
  className?: string;
  style?: CSSProperties;
  id?: string;
  /**
   * words — each word reveals in sequence (default, used for headings)
   * chars — each glyph reveals (logo-like labels)
   * lines — words sharing a rendered line reveal together (measured at play time)
   */
  split?: Split;
  /**
   * blur — rise + de-blur + fade (the reference heading treatment)
   * mask — slide up from behind an overflow mask
   * clip — clip-path wipe from the bottom + lift (the classic editorial line reveal)
   */
  variant?: Variant;
  delay?: number;
  duration?: number;
  stagger?: number;
  /** ScrollTrigger start for self-triggered reveals. */
  start?: string;
  /** "scroll" reveals itself on scroll; "manual" leaves `[data-tr-unit]` for a parent timeline. */
  mode?: "scroll" | "manual";
  /** CSS gradient painted continuously across each rendered line (background-clip: text). */
  gradient?: string;
  /** Extra classes for individual words, e.g. to mute trailing words. */
  wordClass?: (index: number, word: string) => string | undefined;
  /**
   * Classes for the breaks produced by "\n". A hand-placed break is a decision about a
   * wide column, so it is suppressed below `sm` by default: kept, a desktop-tuned break
   * shatters into short fragments in a phone-width column and strands the last word.
   * Pass "" to hold the break at every width.
   */
  breakClassName?: string;
};

export function TextReveal({
  text,
  as: Tag = "p",
  className,
  style,
  id,
  split = "words",
  variant = split === "lines" ? "clip" : "blur",
  delay,
  duration,
  stagger,
  start = "top 82%",
  mode = "scroll",
  gradient,
  wordClass,
  breakClassName = "max-sm:hidden",
}: TextRevealProps) {
  const ref = useRef<HTMLElement>(null);

  // Paint the gradient per line: each unit gets a background sized to its line and
  // offset by its own position, so the fade reads as one continuous sweep per line.
  useIsoLayoutEffect(() => {
    const el = ref.current;
    if (!gradient || !el) return;
    const units = Array.from(el.querySelectorAll<HTMLElement>("[data-tr-unit]"));

    const paint = () => {
      const rows = new Map<number, HTMLElement[]>();
      for (const unit of units) {
        const key = Math.round(unit.offsetTop / 6);
        rows.set(key, [...(rows.get(key) ?? []), unit]);
      }
      rows.forEach((row) => {
        const left = Math.min(...row.map((u) => u.offsetLeft));
        const right = Math.max(...row.map((u) => u.offsetLeft + u.offsetWidth));
        for (const unit of row) {
          unit.style.backgroundSize = `${right - left}px 100%`;
          unit.style.backgroundPosition = `${left - unit.offsetLeft}px 0`;
        }
      });
    };

    paint();
    const ro = new ResizeObserver(paint);
    ro.observe(el);
    return () => ro.disconnect();
  }, [gradient, text]);

  useGSAP(
    () => {
      const el = ref.current;
      if (!el || mode !== "scroll") return;
      const units = gsap.utils.toArray<HTMLElement>("[data-tr-unit]", el);

      if (reduceMotion()) {
        gsap.set(units, { opacity: 1, yPercent: 0, y: 0, clearProps: "clipPath,filter" });
        return;
      }

      const vars: gsap.TweenVars = { scrollTrigger: { trigger: el, start, toggleActions: PLAY_ONCE } };
      if (delay !== undefined) vars.delay = delay;
      if (duration !== undefined) vars.duration = duration;

      if (split === "lines") {
        const tops = units.map((u) => Math.round(u.offsetTop / 6));
        const rows = Array.from(new Set(tops)).sort((a, b) => a - b);
        const perLine = stagger ?? 0.12;
        vars.stagger = (i: number) => rows.indexOf(tops[i]) * perLine;
      } else if (stagger !== undefined) {
        vars.stagger = stagger;
      }

      if (variant === "mask") maskReveal(units, vars);
      else if (variant === "clip") clipReveal(units, vars);
      else blurReveal(units, vars);
    },
    { scope: ref, dependencies: [text] },
  );

  const lines = text.split("\n");
  let wordIndex = -1;

  const unit = (content: ReactNode, key: number) =>
    variant === "mask" ? (
      <span key={key} className="tr-mask">
        <span className="tr-unit" data-tr-unit="">
          {content}
        </span>
      </span>
    ) : (
      <span key={key} className="tr-unit" data-tr-unit="">
        {content}
      </span>
    );

  return (
    <Tag
      ref={ref}
      id={id}
      className={cn("tr", gradient && "tr-gradient", className)}
      style={gradient ? ({ ...style, "--tr-gradient": gradient } as CSSProperties) : style}
      data-variant={variant}
    >
      <span className="sr-only">{text.replace(/\n/g, " ")}</span>
      <span aria-hidden="true">
        {lines.map((line, li) => {
          const words = line.split(" ").filter(Boolean);
          return (
            <Fragment key={li}>
              {words.map((word, wi) => {
                wordIndex += 1;
                return (
                  <Fragment key={wi}>
                    <span className={cn("tr-word", wordClass?.(wordIndex, word))}>
                      {split === "chars" ? Array.from(word).map((ch, ci) => unit(ch, ci)) : unit(word, 0)}
                    </span>
                    {wi < words.length - 1 ? " " : null}
                  </Fragment>
                );
              })}
              {/* The leading space collapses at a line end, but keeps the words apart when
                  `breakClassName` suppresses the break at a narrow width. */}
              {li < lines.length - 1 ? (
                <>
                  {" "}
                  <br className={breakClassName} />
                </>
              ) : null}
            </Fragment>
          );
        })}
      </span>
    </Tag>
  );
}
